import styles from './ProductCardExpander.module.scss';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import {
  Disclosure,
  DisclosureContent,
  DisclosureProvider,
} from '@ariakit/react';
import { Content, FontAwesomeProIcon } from '@wienenergiegithub/ui-next';

const ProductCardExpander = ({ items, isDiscounted, productType }) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const accordionState = useMemo(
    () => ({
      selectedIndex,
      setSelectedIndex,
    }),
    [selectedIndex],
  );

  return items.map(
    (
      {
        expanded,
        ist_rabattiert: istRabattiert,
        kurztext_rabattiert: kurztextRabattiert,
        longtext_rabattiert: longtextRabattiert,
        name: attributeName,
        shortValue,
        verbauchspreise,
      },
      index,
    ) => (
      <ProductCardExpandable
        accordionState={accordionState}
        attributeName={attributeName}
        expanded={expanded}
        index={index}
        isDiscounted={isDiscounted}
        istRabattiert={istRabattiert}
        key={`${shortValue}-${attributeName}`}
        kurztextRabattiert={kurztextRabattiert}
        longtextRabattiert={longtextRabattiert}
        productType={productType}
        shortValue={shortValue}
        zeitVerbrauchspreise={verbauchspreise}
      />
    ),
  );
};

const ProductCardExpandable = ({
  accordionState,
  attributeName,
  expanded,
  index,
  isDiscounted,
  istRabattiert,
  kurztextRabattiert,
  longtextRabattiert,
  productType,
  shortValue,
  zeitVerbrauchspreise,
}) => {
  const isVisible = index === accordionState.selectedIndex;
  const contentRef = useRef(null);
  const [animationState, setAnimationState] = useState(false);
  const isTanke = productType === 'tanke' || productType === 'business_tanke';

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.focus();
    }
  }, [accordionState.selectedIndex]);

  const handleToggle = () => {
    if (!isVisible) {
      accordionState.setSelectedIndex(index);
    } else {
      accordionState.setSelectedIndex(-1);
    }
  };
  const initialRender = useRef(true);

  useEffect(() => {
    let timer = null;
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      if (animationState) {
        setAnimationState(false);
      }
      timer = setTimeout(() => {
        setAnimationState(true);
      }, 100);
    }
    return () => clearTimeout(timer);
  }, [shortValue]);

  const showDiscountLabel =
    isDiscounted &&
    zeitVerbrauchspreise?.some(
      singleVerbrauchspreis =>
        singleVerbrauchspreis?.verbrauchspreis_value_discounted,
    );

  return (
    <div
      className={classNames(styles.attributeWrapper, {
        [styles.animation]: animationState,
      })}
    >
      <DisclosureProvider open={isVisible}>
        <Disclosure
          className={classNames(styles.attributeToggle, {
            [styles.isTanke]: isTanke,
          })}
          render={<div />}
          toggleOnClick={handleToggle}
        >
          <span
            className={classNames(styles.attributeTitle, {
              [styles.isTanke]: isTanke,
            })}
          >
            {attributeName}
            {showDiscountLabel && (
              <>
                {' '}
                - <span className={styles.isDiscounted}>mit Rabatt </span>
              </>
            )}
          </span>
          <span
            className={classNames(styles.attributeShort, {
              [styles.isTanke]: isTanke,
              [styles.animation]: animationState,
              [styles.isDiscounted]: showDiscountLabel,
            })}
          >
            <span
              className={classNames(styles.attributeShort, {
                [styles.rabbatiert]: istRabattiert,
              })}
            >
              {shortValue}
            </span>
            <br />
            <span className={styles.shortTextRabattiert}>
              {istRabattiert && kurztextRabattiert}
            </span>
          </span>
          {!!expanded && (
            <div
              aria-label="Wählen um mehr Informationen zu erhalten"
              className={classNames(styles.attributeDisclosure, {
                [styles.isTanke]: isTanke,
                [styles.disclosureOpen]: isVisible,
              })}
            >
              <FontAwesomeProIcon
                className={styles.disclosureIcon}
                icon="chevron-down"
                role="presentation"
                type="solid"
              />
            </div>
          )}
        </Disclosure>
        {!!expanded && (
          <DisclosureContent
            className={styles.attributeRegion}
            visible={isVisible}
          >
            {props =>
              isVisible && (
                <div {...props}>
                  <Content
                    className={styles.contentContainer}
                    noSpacing
                    ref={contentRef}
                    size="regular"
                    tabIndex={-1}
                    withParser
                  >
                    {!!istRabattiert && longtextRabattiert?.length > 0
                      ? longtextRabattiert
                      : expanded}
                  </Content>

                  {zeitVerbrauchspreise?.length > 1 && (
                    <dl
                      className={classNames(styles.descList, styles.noSpacing)}
                    >
                      {zeitVerbrauchspreise.map(item => (
                        <>
                          <dt className={styles.descTerm}>
                            {item.verbrauchspreis_label}
                          </dt>
                          <dd
                            className={classNames(
                              styles.descDetails,
                              styles.noSpacing,
                            )}
                          >
                            <span className={styles.labelContainer}>
                              {item.verbrauchspreis_value_discounted ??
                                item.verbrauchspreis_value}{' '}
                              {item.verbrauchspreis_unit}
                            </span>
                          </dd>
                        </>
                      ))}
                    </dl>
                  )}
                </div>
              )
            }
          </DisclosureContent>
        )}
      </DisclosureProvider>
    </div>
  );
};

export default ProductCardExpander;
